// CalcGlobe smart-search parser.
//
// Given any free-text query the user types, return up to N ranked results.
// Each result has a stable shape that the search dropdown can render and
// the navigation handler can act on.
//
//   { kind: 'conversion', id, name, category, fromKey, toKey, preview, calcId, params }
//   { kind: 'calculator', id, name, categoryName, calcId, params? }
//
// Supported input formats:
//   • "ft to mm" / "ft → mm" / "ft into mm" / "ft in mm"
//   • "convert feet to millimetres"
//   • "how many litres in a gallon"
//   • single token: "ft", "kg", "°C", "USD", "EMI", "concrete"
//   • full names: "complex number calculator", "iv cannula sizes"
//   • typo-tolerant: "morgage", "concret", "celcus", "kilogrm" (fuzzy fallback)
//
// All comparisons are case-insensitive and whitespace-/punctuation-tolerant.

import { UNIT_ALIASES, CALC_KEYWORDS, SPELLING_FIX } from '@/data/searchDictionary';
import { allCalculators } from '@/data/calculatorRegistry';
import { SLUG_BY_ID } from '@/data/slugs';

// Normalise a single token: lower-case, strip diacritics, drop non-alnum-and-slash-and-dash.
// We preserve `/`, `-` and `.` because some unit codes use them (kg/m3, ft-lb, m/s, ohm.m).
function normToken(t) {
  if (!t) return '';
  let s = t.toLowerCase().trim();
  s = s.replace(/[\s\u00A0]+/g, '');           // strip ALL whitespace
  s = s.replace(/['"`,]/g, '');                // strip quotes
  // Apply spelling fix to the whole token if it's a single dictionary word
  if (SPELLING_FIX[s]) s = SPELLING_FIX[s];
  return s;
}

// Normalise a phrase: lower-case, collapse whitespace, strip filler words but keep "to/in/into".
function normPhrase(q) {
  let s = q.toLowerCase().trim();
  s = s.replace(/[\u00A0]+/g, ' ');
  s = s.replace(/\s+/g, ' ');
  // Strip leading "convert" / "how many" / "a"
  s = s.replace(/^(?:convert|how\s+many|how\s+much|whats|what\s+is|what's)\s+/i, '');
  // "a gallon" → "gallon"
  s = s.replace(/\s+(?:a|an)\s+/g, ' ');
  // "feet in millimetres" → use "in" as a separator; also "into"
  s = s.replace(/\s+(?:into|→|->)\s+/g, ' to ');
  s = s.replace(/\s+in\s+/g, ' to ');
  return s.trim();
}

// ─── Levenshtein edit distance (two-row, O(n·m) time, O(m) space) ───
// Used only by the fuzzy fallback so typos still resolve to the right tool.
function editDistance(a, b) {
  if (a === b) return 0;
  const al = a.length, bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  let prev = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    const cur = [i];
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[bl];
}

// Allowed edits scale with word length: short words tolerate 1, longer tolerate 2.
// Words under 4 chars are never fuzzed (too risky — "ft", "kg", "cm" must stay exact).
function fuzzyThreshold(key) {
  if (key.length < 4) return 0;
  if (key.length <= 6) return 1;
  return 2;
}

// Find the closest unit-alias key to a token within threshold. Returns {alias, dist} or null.
function closestUnitAlias(token) {
  if (token.length < 4) return null;
  let best = null, bestDist = Infinity;
  for (const key of Object.keys(UNIT_ALIASES)) {
    const thr = fuzzyThreshold(key);
    if (thr === 0) continue;
    const d = editDistance(token, key);
    if (d <= thr && d < bestDist) { bestDist = d; best = key; }
  }
  return best ? { alias: best, dist: bestDist } : null;
}

// Look up a token in unit aliases. Returns { c, k } or null.
function lookupUnit(token, hintCategory) {
  const norm = normToken(token);
  if (!norm) return null;
  // Direct hit
  let hit = UNIT_ALIASES[norm];
  // Disambiguate "pound" — could be currency (GBP) or mass (lb); use partner hint.
  if (norm === 'pound' || norm === 'pounds') {
    if (hintCategory === 'currency') return UNIT_ALIASES.pound_ccy;
    return UNIT_ALIASES.lb;
  }
  return hit || null;
}

// Fuzzy variant — only used as a fallback when exact lookup fails on a conversion side.
function lookupUnitFuzzy(token) {
  const norm = normToken(token);
  if (!norm) return null;
  if (UNIT_ALIASES[norm]) return UNIT_ALIASES[norm];
  const near = closestUnitAlias(norm);
  return near ? UNIT_ALIASES[near.alias] : null;
}

// Build a calculator index for O(1) keyword + name lookup.
const CALC_BY_ID = Object.fromEntries(allCalculators.map(c => [c.id, c]));

// Score a calculator by how well its name matches the query.
function scoreCalcByName(calc, q) {
  const name = calc.name.toLowerCase();
  if (name === q) return 100;
  if (name.startsWith(q)) return 80;
  if (name.includes(q)) return 50;
  // Token-set match
  const qTokens = q.split(/\s+/).filter(Boolean);
  const nameTokens = name.split(/[\s\-/&]+/).filter(Boolean);
  const overlap = qTokens.filter(t => nameTokens.some(n => n.startsWith(t))).length;
  if (overlap === qTokens.length && qTokens.length > 0) return 40;
  if (overlap > 0) return 20 * overlap;
  return 0;
}

// Build a result for opening the Unit Converter at a category (no preset units).
function buildUnitCatResult(category, fromKey = null, toKey = null) {
  const isCurrency = category === 'currency';
  const calcId = isCurrency ? 'currency' : 'unit-converter';
  const slug = SLUG_BY_ID[calcId] || 'unit-converter';
  const params = { uc: category };
  if (fromKey) params.from = fromKey;
  if (toKey) params.to = toKey;
  const qs = new URLSearchParams(params).toString();
  const url = `/${slug}?${qs}`;
  return { calcId, params, url, slug };
}

// Friendly display name for a unit code.
const FRIENDLY = {
  ft: 'Foot', mm: 'Millimetre', cm: 'Centimetre', m: 'Metre', km: 'Kilometre',
  in: 'Inch', yd: 'Yard', mi: 'Mile', nmi: 'Nautical mile', um: 'Micrometre', nm: 'Nanometre',
  kg: 'Kilogram', g: 'Gram', mg: 'Milligram', lb: 'Pound', oz: 'Ounce', stone: 'Stone', tonne: 'Tonne', uston: 'US ton',
  C: 'Celsius', F: 'Fahrenheit', K: 'Kelvin',
  L: 'Litre', mL: 'Millilitre', usgal: 'US gallon', ukgal: 'UK gallon',
  ms: 'Metre/second', kmh: 'km/h', mph: 'mph', kn: 'Knot', fts: 'ft/s',
  m2: 'Square metre', ft2: 'Square foot', in2: 'Square inch', km2: 'Square km', acre: 'Acre', ha: 'Hectare',
};
const friendly = (key) => FRIENDLY[key] || key;

const CAT_LABEL = {
  length: 'Length', mass: 'Mass / Weight', temperature: 'Temperature', volume: 'Volume',
  speed: 'Speed', time: 'Time', area: 'Area', data: 'Data / Storage', pressure: 'Pressure',
  energy: 'Energy', power: 'Power', force: 'Force', angle: 'Angle', fuel: 'Fuel Economy',
  torque: 'Torque', voltage: 'Voltage', current: 'Current', resistance: 'Resistance',
  density: 'Density', currency: 'Currency',
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PARSER
// ─────────────────────────────────────────────────────────────────────────────
export function smartSearch(rawQuery, limit = 6) {
  if (!rawQuery || !rawQuery.trim()) return [];
  const phrase = normPhrase(rawQuery);
  const compact = phrase.replace(/\s+/g, '');           // "fttomm" support
  const results = [];

  // ── Step 1 — "X to Y" conversion detection ──
  const toMatch = phrase.match(/^(.+?)\s+to\s+(.+?)$/);
  if (toMatch) {
    const left = toMatch[1].trim();
    const right = toMatch[2].trim();
    // Exact first, then fuzzy-correct each side so "kg to pund" still works.
    let leftHit = lookupUnit(left);
    let rightHit = lookupUnit(right);
    if (!leftHit) leftHit = lookupUnitFuzzy(left);
    if (!rightHit) rightHit = lookupUnit(right, leftHit?.c) || lookupUnitFuzzy(right);
    // Re-resolve "pound" on the right using the left side's category as a hint.
    if (leftHit && (right === 'pound' || right === 'pounds')) {
      rightHit = lookupUnit(right, leftHit.c);
    }

    if (leftHit && rightHit && leftHit.c === rightHit.c) {
      const cat = leftHit.c;
      const r = buildUnitCatResult(cat, leftHit.k, rightHit.k);
      results.push({
        kind: 'conversion',
        id: `conv-${cat}-${leftHit.k}-${rightHit.k}`,
        name: `${friendly(leftHit.k)} → ${friendly(rightHit.k)}`,
        category: CAT_LABEL[cat] || cat,
        preview: `${left.toUpperCase()} → ${right.toUpperCase()}`,
        calcId: r.calcId, url: r.url, params: r.params,
        icon: cat === 'currency' ? '💱' : '🔄',
        score: 200,
      });
    } else if (leftHit && !rightHit) {
      const r = buildUnitCatResult(leftHit.c, leftHit.k, null);
      results.push({
        kind: 'unitcat',
        id: `cat-${leftHit.c}-${leftHit.k}`,
        name: `${CAT_LABEL[leftHit.c] || leftHit.c} (from ${friendly(leftHit.k)})`,
        category: 'Unit Converter',
        calcId: r.calcId, url: r.url, params: r.params,
        icon: '🔄', score: 90,
      });
    } else if (!leftHit && rightHit) {
      const r = buildUnitCatResult(rightHit.c, null, rightHit.k);
      results.push({
        kind: 'unitcat',
        id: `cat-${rightHit.c}-${rightHit.k}`,
        name: `${CAT_LABEL[rightHit.c] || rightHit.c} (to ${friendly(rightHit.k)})`,
        category: 'Unit Converter',
        calcId: r.calcId, url: r.url, params: r.params,
        icon: '🔄', score: 90,
      });
    }
  }

  // ── Step 2 — Single-token unit (e.g. "ft", "kg", "USD") ──
  if (!toMatch) {
    const tokenHit = UNIT_ALIASES[normToken(phrase)] || UNIT_ALIASES[compact];
    if (tokenHit) {
      const r = buildUnitCatResult(tokenHit.c, tokenHit.k, null);
      results.push({
        kind: 'unitcat',
        id: `cat-${tokenHit.c}-${tokenHit.k}`,
        name: `${CAT_LABEL[tokenHit.c] || tokenHit.c} — ${friendly(tokenHit.k)}`,
        category: 'Unit Converter',
        calcId: r.calcId, url: r.url, params: r.params,
        icon: tokenHit.c === 'currency' ? '💱' : '🔄',
        score: 80,
      });
    }
  }

  // ── Step 3 — Calculator keyword lookup (whole-phrase, then compact) ──
  const keywordHits = new Set();
  const tryKeyword = (k) => { const id = CALC_KEYWORDS[k]; if (id) keywordHits.add(id); };
  tryKeyword(normToken(phrase));
  tryKeyword(compact);
  phrase.split(/[\s\-/]+/).filter(Boolean).forEach(t => tryKeyword(normToken(t)));
  keywordHits.forEach(calcId => {
    const calc = CALC_BY_ID[calcId];
    if (!calc) return;
    const slug = SLUG_BY_ID[calcId] || calcId;
    results.push({
      kind: 'calculator',
      id: `calc-${calcId}`,
      name: calc.name,
      categoryName: calc.categoryName || '',
      calcId,
      url: `/${slug}`,
      params: {},
      icon: '🧮',
      score: 70,
    });
  });

  // ── Step 4 — Calculator NAME fuzzy match ──
  const q = phrase.toLowerCase();
  for (const calc of allCalculators) {
    const s = scoreCalcByName(calc, q);
    if (s > 0) {
      const slug = SLUG_BY_ID[calc.id] || calc.id;
      results.push({
        kind: 'calculator',
        id: `name-${calc.id}`,
        name: calc.name,
        categoryName: calc.categoryName || '',
        calcId: calc.id,
        url: `/${slug}`,
        params: {},
        icon: '🧮',
        score: s,
      });
    }
  }

  // ── Step 5 — Fuzzy fallback (typo tolerance) ──
  // Runs only when exact matching found little, so it never adds noise to good queries.
  // Catches misspellings the hardcoded SPELLING_FIX list doesn't: "morgage", "concret"…
  if (results.length < 3 && !toMatch && compact.length >= 4) {
    // (a) Fuzzy unit alias — e.g. "kilogrm" → kilogram
    const nearUnit = closestUnitAlias(compact);
    if (nearUnit) {
      const hit = UNIT_ALIASES[nearUnit.alias];
      const r = buildUnitCatResult(hit.c, hit.k, null);
      results.push({
        kind: 'unitcat',
        id: `cat-${hit.c}-${hit.k}`,
        name: `${CAT_LABEL[hit.c] || hit.c} — ${friendly(hit.k)}`,
        category: 'Unit Converter',
        calcId: r.calcId, url: r.url, params: r.params,
        icon: hit.c === 'currency' ? '💱' : '🔄',
        score: 34 - nearUnit.dist,   // below any exact hit
      });
    }

    // (b) Fuzzy calculator keyword — e.g. "morgage" → mortgage → EMI/mortgage calc
    let bestKw = null, bestKwDist = Infinity;
    for (const key of Object.keys(CALC_KEYWORDS)) {
      const thr = fuzzyThreshold(key);
      if (thr === 0) continue;
      const d = editDistance(compact, key);
      if (d <= thr && d < bestKwDist) { bestKwDist = d; bestKw = key; }
    }
    if (bestKw) {
      const calcId = CALC_KEYWORDS[bestKw];
      const calc = CALC_BY_ID[calcId];
      if (calc) {
        const slug = SLUG_BY_ID[calcId] || calcId;
        results.push({
          kind: 'calculator',
          id: `fuzzy-${calcId}`,
          name: calc.name,
          categoryName: calc.categoryName || '',
          calcId,
          url: `/${slug}`,
          params: {},
          icon: '🧮',
          score: 36 - bestKwDist,    // below any exact hit
        });
      }
    }
  }

  // ── De-duplicate by id (keep highest score) and sort ──
  const dedup = new Map();
  for (const r of results) {
    const prev = dedup.get(r.id);
    if (!prev || r.score > prev.score) dedup.set(r.id, r);
  }
  // Also de-duplicate by calcId+url so a "conversion" result doesn't get
  // shadowed by a generic "unit-converter" calculator name match.
  const byUrl = new Map();
  for (const r of dedup.values()) {
    const key = r.url || r.calcId;
    const prev = byUrl.get(key);
    if (!prev || r.score > prev.score) byUrl.set(key, r);
  }

  return Array.from(byUrl.values()).sort((a, b) => b.score - a.score).slice(0, limit);
}
